import React, { useState, useEffect, useRef } from 'react';
import { Upload, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { coreServicesApi, handleApiError } from '../lib/api-client';
import { useAuth } from '../contexts/AuthContext';

interface ProcessImageResult {
  messageId: string;
  analysis: {
    isLewd: boolean;
    description: string;
    confidence?: number;
    reasoning?: string;
    isGodMode?: boolean;
  };
  response: {
    text: string;
    provider: string;
    promptType: string;
  };
  imageUrl: string;
  errors?: Array<{ stage: string; error: string }>;
}

interface ImageUpload {
  file: File;
  preview: string;
  uploadedUrl: string | null;
  result: ProcessImageResult | null;
  isUploading: boolean;
  isProcessing: boolean;
  error: string;
}

export const UploadPage: React.FC = () => {
  const { token } = useAuth();
  const resultsRef = useRef<HTMLDivElement>(null);
  const [images, setImages] = useState<ImageUpload[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Sassy messages for loading state
  const [sassyMessage, setSassyMessage] = useState('Processing...');

  const SASSY_MESSAGES = [
    "Judging your life choices...",
    "Scanning for depravity...",
    "Consulting the Kink Council...",
    "Calculating cringe levels...",
    "Preparing to roast you...",
    "Reviewing with disgust...",
    "Oh my, what do we have here...",
  ];

  const pickSassyMessage = () => {
    const random = SASSY_MESSAGES[Math.floor(Math.random() * SASSY_MESSAGES.length)];
    setSassyMessage(random);
  };

  // Auto-scroll to results when they load on mobile
  useEffect(() => {
    const currentImage = images[currentImageIndex];
    if (currentImage?.result && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [images, currentImageIndex]);

  // 1. Upload Temp (now handles specific image index)
  const uploadImage = async (index: number, base64Image: string) => {
    setImages(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isUploading: true, error: '' };
      return updated;
    });

    try {
      const data = await coreServicesApi.praiseKink.uploadTemp({
        image: base64Image,
        userId: `web_user_${token?.split('-')[1] || 'unknown'}`,
      });
      
      setImages(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], uploadedUrl: data.url, isUploading: false };
        return updated;
      });
    } catch (err: any) {
      setImages(prev => {
        const updated = [...prev];
        updated[index] = { 
          ...updated[index], 
          isUploading: false, 
          error: `Upload failed: ${handleApiError(err)}` 
        };
        return updated;
      });
    }
  };

  // 2. Process image (analysis + response generation + saving in one call)
  const processImage = async (index: number) => {
    const image = images[index];
    if (!image?.uploadedUrl) return;

    pickSassyMessage();
    setImages(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isProcessing: true, error: '' };
      return updated;
    });

    try {
      const data = await coreServicesApi.praiseKink.processImage({
        imageUrl: image.uploadedUrl,
        userId: `web_user_${token?.split('-')[1] || 'unknown'}`,
        channelId: 'web_ui',
        guildId: 'web_ui',
        sensitivity: 'moderate',
      });

      setImages(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], result: data, isProcessing: false };
        return updated;
      });

      // Log any non-critical errors
      if (data.errors && data.errors.length > 0) {
        console.warn('Image processing had non-critical errors:', data.errors);
      }
    } catch (err) {
      setImages(prev => {
        const updated = [...prev];
        updated[index] = { 
          ...updated[index], 
          isProcessing: false, 
          error: `Processing failed: ${handleApiError(err)}` 
        };
        return updated;
      });
    }
  };

  /**
   * Compress and resize image using Canvas
   */
  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const MAX_SIZE = 1024; // Standardize to max 1024px

          if (width > height) {
            if (width > MAX_SIZE) {
              height *= MAX_SIZE / width;
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width *= MAX_SIZE / height;
              height = MAX_SIZE;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          // Compress to JPEG with 0.8 quality
          // This drastically reduces size compared to raw PNG/base64
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve(compressedDataUrl);
        };
        img.onerror = (err) => reject(err);
      };
      reader.onerror = (err) => reject(err);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    
    if (files.length === 0) return;

    // Create placeholder entries for all files
    const newImages: ImageUpload[] = files.map(file => ({
      file,
      preview: '',
      uploadedUrl: null,
      result: null,
      isUploading: false,
      isProcessing: false,
      error: '',
    }));

    setImages(newImages);
    setCurrentImageIndex(0);

    // Compress and upload all images
    for (let i = 0; i < files.length; i++) {
      try {
        const compressed = await compressImage(files[i]);
        setImages(prev => {
          const updated = [...prev];
          updated[i] = { ...updated[i], preview: compressed };
          return updated;
        });

        // Start upload immediately after compression
        await uploadImage(i, compressed);
      } catch (err) {
        console.error(`Failed to compress image ${i}:`, err);
        setImages(prev => {
          const updated = [...prev];
          updated[i] = { ...updated[i], error: 'Failed to process image' };
          return updated;
        });
      }
    }
  };

  const handleAnalyzeAll = async () => {
    // Process all images that have been uploaded but not yet processed
    for (let i = 0; i < images.length; i++) {
      if (images[i].uploadedUrl && !images[i].result && !images[i].isProcessing) {
        await processImage(i);
      }
    }
  };

  const handleReset = () => {
    setImages([]);
    setCurrentImageIndex(0);
  };

  const currentImage = images[currentImageIndex];
  const hasMultipleImages = images.length > 1;
  const allUploaded = images.length > 0 && images.every(img => img.uploadedUrl !== null);
  const anyProcessing = images.some(img => img.isProcessing);
  const anyUploading = images.some(img => img.isUploading);

  const getStatusText = () => {
    if (anyUploading) return 'Uploading...';
    if (anyProcessing) return sassyMessage;
    return images.length > 1 ? 'Analyze All Images' : 'Analyze Image';
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-700 bg-gray-850 px-4 py-4 md:px-8 md:py-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Come Get Your Lewds Graded, Fuckbois & Fuckgirls</h1>
            {hasMultipleImages && (
              <p className="text-sm text-gray-400 mt-1">Uploaded {images.length} images (max 5)</p>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Full width two-pane */}
      <div className="flex-1 flex flex-col lg:flex-row gap-8 p-4 md:p-8 overflow-hidden">
        {/* Upload & Preview - Left Pane */}
        <div className="flex-1 flex flex-col">
          <div className="bg-gray-850 border border-gray-700 rounded-lg p-6 md:p-8 flex flex-col h-full">
            <div className="space-y-4 flex-1 flex flex-col">
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-600 hover:border-pink-500 rounded-lg p-8 cursor-pointer transition-colors flex-1">
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm font-medium">Click to upload images (up to 5)</span>
                <span className="text-xs text-gray-500 mt-1">PNG, JPG, or WebP</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  disabled={anyProcessing || anyUploading}
                  className="hidden"
                />
              </label>

              {currentImage && (
                <div className="space-y-2 flex-1 flex flex-col">
                  {/* Image carousel navigation */}
                  {hasMultipleImages && (
                    <div className="flex items-center justify-between text-sm text-gray-400">
                      <button
                        onClick={() => setCurrentImageIndex(Math.max(0, currentImageIndex - 1))}
                        disabled={currentImageIndex === 0}
                        className="p-2 hover:bg-gray-800 rounded disabled:opacity-30 disabled:cursor-not-allowed transition"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <span>Image {currentImageIndex + 1} of {images.length}</span>
                      <button
                        onClick={() => setCurrentImageIndex(Math.min(images.length - 1, currentImageIndex + 1))}
                        disabled={currentImageIndex === images.length - 1}
                        className="p-2 hover:bg-gray-800 rounded disabled:opacity-30 disabled:cursor-not-allowed transition"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  )}

                  <img
                    src={currentImage.preview}
                    alt="Preview"
                    className="w-full h-auto rounded-lg border border-gray-700 object-contain"
                  />
                  
                  {currentImage.error && (
                    <div className="bg-red-500/10 border border-red-500/50 rounded p-2 text-red-400 text-xs">
                      {currentImage.error}
                    </div>
                  )}
                  
                  {currentImage.isUploading && (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </div>
                  )}

                  <button
                    onClick={handleReset}
                    disabled={anyProcessing || anyUploading}
                    className="w-full px-3 py-2 text-sm bg-gray-800 hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                  >
                    Change Images
                  </button>
                </div>
              )}

              <button
                onClick={handleAnalyzeAll}
                disabled={!allUploaded || anyProcessing || anyUploading}
                className="w-full px-4 py-3 font-medium rounded-lg transition-colors flex items-center justify-center gap-2 bg-pink-600 hover:bg-pink-700 text-white disabled:opacity-50 disabled:cursor-not-allowed mt-auto"
              >
                {(anyProcessing || anyUploading) ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {getStatusText()}
                  </>
                ) : (
                  getStatusText()
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Results - Right Pane */}
        <div ref={resultsRef} className="flex-1 flex flex-col overflow-y-auto">
          {currentImage?.result && (
            <div
              className={`border rounded-xl p-8 shadow-2xl transition-all duration-500 transform scale-100 flex flex-col h-full ${currentImage.result.analysis.isLewd
                  ? 'bg-gradient-to-br from-pink-900/90 to-purple-900/90 border-pink-400/50 shadow-pink-500/20'
                  : 'bg-gradient-to-br from-red-900/90 to-orange-900/90 border-red-500/50 shadow-red-500/20'
                }`}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-2xl font-bold ${currentImage.result.analysis.isLewd ? 'text-pink-100' : 'text-red-100'}`}>
                  {currentImage.result.analysis.isLewd ? '💋 Verdict' : '💢 Verdict'}
                </h3>
                <span className={`px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wider ${currentImage.result.analysis.isLewd
                    ? 'bg-pink-500/30 text-pink-200 border border-pink-400/30'
                    : 'bg-red-500/30 text-red-200 border border-red-400/30'
                  }`}>
                  {currentImage.result.analysis.isLewd ? 'Lewd' : 'Roasted'}
                </span>
              </div>

              <div className="prose prose-invert max-w-none mb-8 flex-1 overflow-y-auto">
                <p className={`text-lg leading-relaxed whitespace-pre-wrap ${currentImage.result.analysis.isLewd ? 'text-pink-50' : 'text-red-50'}`}>
                  {currentImage.result.response.text}
                </p>
              </div>

              {hasMultipleImages && (
                <div className="mb-4 flex gap-2 justify-center">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-3 h-3 rounded-full transition ${
                        idx === currentImageIndex
                          ? currentImage.result?.analysis.isLewd
                            ? 'bg-pink-400'
                            : 'bg-red-400'
                          : 'bg-gray-600 hover:bg-gray-500'
                      }`}
                    />
                  ))}
                </div>
              )}

              <button
                onClick={handleReset}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] mt-auto ${currentImage.result.analysis.isLewd
                    ? 'bg-pink-500 hover:bg-pink-400 text-white shadow-lg shadow-pink-500/30'
                    : 'bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-600/30'
                  }`}
              >
                Analyze More Images
              </button>
            </div>
          )}

          {currentImage && !currentImage.result && currentImage.preview && !currentImage.isProcessing && (
            <div className="bg-gray-850/50 border border-gray-700/50 rounded-xl p-8 text-center animate-pulse flex items-center justify-center h-full">
              <p className="text-gray-400 text-lg font-medium">Ready for judgment? Hit Analyze.</p>
            </div>
          )}

          {!currentImage && (
            <div className="bg-gray-850/50 border border-gray-700/50 rounded-xl p-8 text-center flex items-center justify-center h-full">
              <p className="text-gray-400 text-lg font-medium">Upload images to see results here~</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
